/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, useState } from "react"
import { Table, Tag } from "@duxton-web/ui"
import { ArrowRightUniversal } from "@duxton-web/icons"
import { OutletStore } from "types/models/menu"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { FilterDropdownDuxton } from "components/dropdowns"
import { TableProps } from "@duxton-web/ui/es/table"
import { Config } from "constants/config"
import { StoreFilter } from "order-manager/types/models/order_common"
import LoadMore from "order-manager/pages/store/list/LoadMore"

const ALL = "ALL"
const ALL_ARRAY = [ALL]

type CityList = { value: string; text: string }

interface StoreListProps extends TableProps<OutletStore> {
  onClickStore?(merchantID: string): void
  onSelectCityFilter?(filter: StoreFilter): void
  onChangeFilterCityVisible?(visible: boolean): void
  hideStoreNameArrowRight?: boolean
  itemsWithoutPhotoCount?: number
  showLoadMore: boolean
  isLoading: boolean
  onLoadMore: () => void
  filter: StoreFilter
  cityList?: CityList[]
}

export const StoreList: FC<StoreListProps> = ({
  dataSource,
  onSelectCityFilter,
  onChangeFilterCityVisible,
  hideStoreNameArrowRight,
  itemsWithoutPhotoCount,
  showLoadMore,
  onLoadMore,
  isLoading,
  filter,
  cityList = [],
  ...props
}: StoreListProps) => {
  const [cityFilterDropdownVisible, setCityFilterDropdownVisible] = useState(
    false,
  )

  const textMap = useTranslationsMap({
    photosMissingWithCount: "label.photoMissing.withCount",
  })

  const handleSetFilter = (city: string[]) => {
    onSelectCityFilter?.({ ...filter, city })
  }

  const handleCityFilterVisibleChange = (visible: boolean) => {
    setCityFilterDropdownVisible(visible)
    onChangeFilterCityVisible?.(visible)
  }

  const renderStoreNameCol = (text: string) => {
    return (
      <div
        css={css`
          .edit_hover_class {
            padding-left: 5px;
          }
          &:not(:hover) {
            .edit_hover_class {
              visibility: hidden;
            }
          }
        `}
      >
        <span
          css={css`
            color: #363a45;
          `}
        >
          {text}
        </span>
        {props.rowSelection?.type !== "checkbox" && !hideStoreNameArrowRight && (
          <span className="edit_hover_class">
            <ArrowRightUniversal style={{ color: "#0091FF" }} />
          </span>
        )}
      </div>
    )
  }

  const renderLoadMore = (text: string, _: OutletStore, index: number) => {
    const lastItem = index === (dataSource || []).length - 1
    return (
      <div>
        {text}
        {lastItem && showLoadMore && (
          <div className="load-more-container">
            <LoadMore onLoadMore={onLoadMore} />
          </div>
        )}
      </div>
    )
  }

  return (
    <Table
      style={{ width: "100%" }}
      dataSource={dataSource}
      pagination={false}
      rowKey="merchantID"
      locale={{
        emptyText: useTranslation("menu.item.modifierGroup.noDataAvailable"),
        filterConfirm: useTranslation("menu.stores.filter.ok"),
      }}
      loading={isLoading}
      scroll={{
        y: Number(window.innerHeight - (itemsWithoutPhotoCount ? 435 : 315)),
      }}
      css={css`
        .store-list-name:not(:hover) {
          .edit_hover_class {
            visibility: hidden;
          }
        }
        ${showLoadMore &&
          `
          tbody>tr:last-of-type {
            position: relative;
            > td {
              position: unset;
              padding-bottom: 80px;
            }
            .load-more-container {
              position: absolute;
              bottom: 0;
              left: 0;
              width: 100%;
              padding: 10px;
              display: flex;
              justify-content: center;
            }
          }
        }
        `}
      `}
      {...props}
    >
      <Table.Column
        title={useTranslation(
          "menu.stores.table.header.storeName",
        ).toUpperCase()}
        dataIndex="merchantName"
        data-testid="store-name-column"
        render={renderStoreNameCol}
        sorter={(a: OutletStore, b: OutletStore) =>
          a.merchantName.localeCompare(b.merchantName)
        }
      />
      <Table.Column
        title={useTranslation("menu.stores.table.header.city").toUpperCase()}
        dataIndex="city"
        data-testid="city-filter-column"
        onFilterDropdownOpenChange={handleCityFilterVisibleChange}
        filterDropdownOpen={cityFilterDropdownVisible}
        filterDropdown={
          <FilterDropdownDuxton
            key={`storeFilter-${Date.now()}`}
            items={[
              {
                text: useTranslation("menu.stores.dropdown.allCities"),
                value: ALL,
              },
              ...cityList,
            ]}
            filteredValue={filter?.city?.length ? filter?.city : ALL_ARRAY}
            setVisible={setCityFilterDropdownVisible}
            onChange={handleSetFilter}
            filterConfirmText={useTranslation("menu.stores.filter.ok")}
          />
        }
      />
      <Table.Column
        title={useTranslation("menu.stores.table.header.street").toUpperCase()}
        dataIndex="address"
        render={renderLoadMore}
      />
      {Config.enableItemPhotoCoverage && !!itemsWithoutPhotoCount && (
        <Table.Column
          dataIndex="itemsWithoutPhotosCount"
          render={(text: number) => {
            return (
              text &&
              text !== 0 && (
                <Tag color="warning">
                  {textMap.photosMissingWithCount.replace(
                    "{x}",
                    text.toString(),
                  )}
                </Tag>
              )
            )
          }}
        />
      )}
    </Table>
  )
}
